import { useEffect, useState } from "react";
import LoadingSection from "../LoadingSection";
import Error from "../Error";
import CustomerAddressesPool from "./CustomerAddressesPool";
import CustomerContactsPool from "./CustomerContactsPool";
import CustomerBanksPool from "./CustomerBanksPool";
import CustomSelectElement from "../CustomSelectElement";
import { selectAccountData } from "../../redux/main/accountSlice";
import Loader from "../Loader";
import CustomToggleSwitch from "../CustomToggleSwitch";
import {
    getCustomerDetails,
    selectCustomerDetailsData,
    selectCustomerDetailsError,
    selectCustomerDetailsIsLoading
} from "../../redux/customers/customerDetailsSlice";
import {
    selectRightModalOldState,
    setGoingToClose,
    setHasToRefresh,
    setSwap,
    setModal,
    swapModal
} from "../../redux/rightModalSlice";
import { useDispatch, useSelector } from "react-redux";
import { getNodeApiUrl } from "../../config/api.config";

export default function AddEditCustomer({ customerId, isProspect = false }) {
    // Chiamata iniziale per ricevere i dettagli del customer
    const accountData = useSelector(selectAccountData); // Questo mi server per dati sui tipi di pagamento
    const token = localStorage.getItem('installamelo-auth-token') ?? '';
    const [customerDataInput, setCustomerDataInput] = useState({
        // Questo è impostato inizialmente perché in backend è obbligatorio questo campo
        contactsList: [{
            name: "",
            surname: "",
            role: "",
            phone: "",
            mobile: "",
            email: "",
            contactTypeLabel: ""
        }],
        // Il flag prospect viene impostato in base alla prop isProspect
        prospect: isProspect
    });
    
    // Inserisco accountId appena caricato da Redux
    useEffect(() => {
        if (accountData) {
            setCustomerDataInput((prevState) => ({
                ...prevState,
                accountId: accountData._id
            }))
        }
    }, [accountData])
    const dispatch = useDispatch();
    const customerDetailsData = useSelector(selectCustomerDetailsData);
    const customerDetailsIsLoading = useSelector(selectCustomerDetailsIsLoading);
    const customerDetailsError = useSelector(selectCustomerDetailsError);
    useEffect(() => {
        if (customerId) dispatch(getCustomerDetails({ customerId, token }))
    }, [customerId])
    // Ricevuti i dati customer da Redux imposto il customerDataInput per la modifica
    // Solo se siamo in modalità edit (customerId è presente)
    useEffect(() => {
        if (customerDetailsData && customerId) {
            setCustomerDataInput((prevState) => ({
                ...prevState,
                ...customerDetailsData
            }))
        } else if (!customerId) {
            // Reset del form quando si apre in modalità 'add'
            setCustomerDataInput({
                contactsList: [{
                    name: "",
                    surname: "",
                    role: "",
                    phone: "",
                    mobile: "",
                    email: "",
                    contactTypeLabel: ""
                }],
                prospect: isProspect,
                accountId: accountData?._id
            });
        }
    }, [customerDetailsData, customerId, isProspect, accountData])

    // Funzione generica per aggiornare il customerDataInput
    const handleCustomerDataInput = (event) => {
        const { id, value } = event.target;
        setCustomerDataInput((prevState) => ({
            ...prevState,
            [id]: value
        }))
    }
    // Funzione modulare unica per le pool Addresses, Contacts e Banks
    // Da passare alle Pool per aggiungere/modificare un indirizzo, un contatto o una banca
    // Gestisce gli array addresses, contactsList o bankLists
    const addEditItem = (arrayName, newObject, index) => {
        setCustomerDataInput((prevState) => {
            // Se l'array è inizialmente vuoto, creo un nuovo array
            // Contenente il nuovo oggetto
            if (!prevState[arrayName]) {
                return {
                    ...prevState,
                    [arrayName]: [newObject]
                }
            };
            // Se è fornito un indice, sostituisco l'elemento a quell'indice
            if (index !== undefined && index !== null) {
                const updatedArray = [...prevState[arrayName]];
                updatedArray[index] = newObject;
                return {
                    ...prevState,
                    [arrayName]: updatedArray
                }
            };
            // Altrimenti aggiungo il nuovo oggetto alla fine dell'array
            return {
                ...prevState,
                [arrayName]: [...prevState[arrayName], newObject]
            }
        })
    }
    // Funzione da passare alla Pool per eliminare un elemento
    // Fra addresses, contactsList o banksList
    const deleteItem = (arrayName, index) => {
        const updatedArray = [...customerDataInput[arrayName]];
        updatedArray.splice(index, 1);
        setCustomerDataInput((prevState) => ({
            ...prevState,
            [arrayName]: updatedArray
        }))
    }

    // Funzione per aggiornare il metodo di pagamento
    const handlePaymentType = (value, label) => {
        setCustomerDataInput((prevState) => ({
            ...prevState,
            paymentLabel: label,
            paymentType: value
        }))
    }

    // Funzione per aggiornare il business type
    const handleBusinessType = (value) => {
        setCustomerDataInput((prevState) => ({
            ...prevState,
            businessType: value
        }))
    }

    const [sendFetchStatus, setSendFetchStatus] = useState('i');
    const [sendError, setSendError] = useState('');
    
    // Stati per il controllo duplicati
    const [duplicateCheckLoading, setDuplicateCheckLoading] = useState(false);
    const [showDuplicateModal, setShowDuplicateModal] = useState(false);
    const [duplicates, setDuplicates] = useState([]);
    const [proceedWithSave, setProceedWithSave] = useState(false);

    // Stati per la propagazione delle correzioni anagrafica/indirizzi
    const [propagationCheckLoading, setPropagationCheckLoading] = useState(false);
    const [showPropagationModal, setShowPropagationModal] = useState(false);
    const [propagationImpact, setPropagationImpact] = useState(null);
    
    // Funzione per controllare i duplicati
    const checkForDuplicates = async () => {
        console.log('[checkForDuplicates] Inizio controllo duplicati...');
        console.log('[checkForDuplicates] customerDataInput:', customerDataInput);
        setDuplicateCheckLoading(true);
        try {
            const url = getNodeApiUrl('/customers/check-duplicates');
            console.log('[checkForDuplicates] URL:', url);
            const response = await fetch(url, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    'auth-token': token
                },
                body: JSON.stringify({ 
                    customer: customerDataInput,
                    similarityThreshold: 0.75,
                    excludeCustomerId: customerId // Escludi il cliente corrente in modifica
                })
            });
            
            console.log('[checkForDuplicates] Response status:', response.status);
            
            if (!response.ok) {
                const errorText = await response.text();
                console.error('[checkForDuplicates] Errore response:', errorText);
                throw new Error('Errore nel controllo duplicati');
            }
            
            const result = await response.json();
            console.log('[checkForDuplicates] Risultato:', result);
            setDuplicateCheckLoading(false);
            
            if (result.hasDuplicates) {
                console.log('[checkForDuplicates] Trovati duplicati, mostro modale...');
                setDuplicates(result.duplicates);
                setShowDuplicateModal(true);
                console.log('[checkForDuplicates] showDuplicateModal impostato a true');
                return true; // Ha trovato duplicati
            }
            
            return false; // Nessun duplicato
        } catch (error) {
            console.error('[checkForDuplicates] Errore:', error);
            setDuplicateCheckLoading(false);
            return false; // In caso di errore, procedi comunque
        }
    };
    
    // Funzione per procedere con il salvataggio dopo conferma
    const handleProceedWithSave = () => {
        setShowDuplicateModal(false);
        setProceedWithSave(true);
    };
    
    // Effect per procedere con il salvataggio dopo conferma
    useEffect(() => {
        if (proceedWithSave) {
            setProceedWithSave(false);
            performSave();
        }
    }, [proceedWithSave]);
    
    // Verifica l'impatto della propagazione delle correzioni (solo in modifica)
    // Ritorna l'oggetto impact { impianti, commesse, assistenze, manutenzioni, appuntamenti } oppure null
    const checkPropagationImpact = async () => {
        setPropagationCheckLoading(true);
        try {
            const url = `${import.meta.env.VITE_API_BASE_URL}/customers/propagationImpact`;
            const response = await fetch(url, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    'auth-token': token
                },
                body: JSON.stringify({ customer: customerDataInput })
            });
            setPropagationCheckLoading(false);
            if (!response.ok) {
                // In caso di errore non blocchiamo il salvataggio: procederà senza propagazione
                console.error('[checkPropagationImpact] Errore nella verifica impatto');
                return null;
            }
            const result = await response.json();
            return result?.impact || null;
        } catch (error) {
            console.error('[checkPropagationImpact] Errore:', error);
            setPropagationCheckLoading(false);
            return null;
        }
    };

    // Somma totale delle occorrenze impattate
    const totalImpact = (impact) => {
        if (!impact) return 0;
        return (impact.impianti || 0) + (impact.commesse || 0) + (impact.assistenze || 0) + (impact.manutenzioni || 0) + (impact.appuntamenti || 0);
    };

    // Conferma la propagazione dalla modale: procede al salvataggio con propagate=true
    const handleConfirmPropagation = () => {
        setShowPropagationModal(false);
        performSave(true);
    };

    // Funzione principale di salvataggio (chiamata dopo controllo duplicati)
    const performSave = async (propagate = false) => {
        // Verifica che la ragione sociale sia compilata per i clienti business
        if (customerDataInput.business && (!customerDataInput.company || customerDataInput.company.trim() === '')) {
            setSendFetchStatus('f');
            setSendError('La ragione sociale è obbligatoria per i clienti business');
            // Evidenzia visivamente il campo obbligatorio
            const companyInput = document.getElementById('company');
            if (companyInput) {
                companyInput.classList.add('border-danger');
                companyInput.focus();
                // Rimuovi la classe dopo 3 secondi
                setTimeout(() => {
                    companyInput.classList.remove('border-danger');
                }, 3000);
            }
            return; // Interrompi l'esecuzione
        }
        
        // Verifica che ci sia almeno un indirizzo quando si crea un nuovo cliente
        if (!customerId && (!customerDataInput.addresses || customerDataInput.addresses.length === 0)) {
            setSendFetchStatus('f');
            setSendError('È obbligatorio inserire almeno un indirizzo per il cliente');
            // Scorri fino alla sezione indirizzi
            const addressSection = document.querySelector('[data-section="addresses"]');
            if (addressSection) {
                addressSection.scrollIntoView({ behavior: 'smooth', block: 'center' });
                addressSection.classList.add('border-danger');
                setTimeout(() => {
                    addressSection.classList.remove('border-danger');
                }, 3000);
            }
            return; // Interrompi l'esecuzione
        }
        
        // In modifica: verifica se la correzione impatta impianti/documenti collegati.
        // Se sì e non è ancora stata confermata la propagazione, mostra la modale di conferma.
        if (customerId && !propagate) {
            const impact = await checkPropagationImpact();
            if (totalImpact(impact) > 0) {
                setPropagationImpact(impact);
                setShowPropagationModal(true);
                return; // Attende conferma dell'utente
            }
        }

        setSendFetchStatus('l');
        const url = `${import.meta.env.VITE_API_BASE_URL}/customers`;
        const bodyJson = { customer: customerDataInput, propagate }
        const headers = {
            'Content-Type': 'application/json',
            'auth-token': token
        };
        const options = {
            method: customerId ? 'PATCH' : 'POST',
            headers,
            body: JSON.stringify(bodyJson)
        }
        try {
            const response = await fetch(url, options);
            if (!response.ok) {
                const errorData = await response.json();
                throw errorData;
            } else {
                const resultData = await response.json();
                console.log('resultData: ', resultData)
                setSendFetchStatus('s');
                
                // Se è una creazione (non edit), salva l'ID del cliente creato nel campo data
                if (!customerId && resultData && resultData._id) {
                    console.log('Cliente creato con ID:', resultData._id);
                    // Salva i dati del cliente creato per permettere la selezione automatica
                    dispatch(setModal({
                        mode: 'add',
                        data: {
                            customerId: resultData._id,
                            customerLabel: resultData.business
                                ? resultData.company
                                : `${resultData.name || ''} ${resultData.surname || ''}`.trim()
                        }
                    }));
                }

                dispatch(setHasToRefresh());

                // Auto-close the modal after successful save (both edit and add)
                setTimeout(() => {
                    // Se c'e' uno stato precedente (modale aperta da un'altra modale), torna indietro
                    if (rightModalOldState && rightModalOldState.oldMode) {
                        dispatch(swapModal({
                            createdProspect: resultData
                        }));
                    } else {
                        dispatch(setGoingToClose());
                    }
                }, 1500);
            }
        } catch (error) {
            console.error(error.message || "Errore da parte del server.");
            setSendFetchStatus('f');
            setSendError(error.message || "Errore da parte del server.");
        }
    };
    
    // Funzione per verificare unicità email area riservata
    const checkReservedAreaEmails = async () => {
        // Trova i contatti con accesso all'area riservata
        const contactsWithAccess = (customerDataInput.contactsList || []).filter(c => c.enabledToReservedArea && c.email);
        if (contactsWithAccess.length === 0) return { hasConflict: false };
        
        try {
            const url = getNodeApiUrl('/customers/check-reserved-emails');
            const response = await fetch(url, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    'auth-token': token
                },
                body: JSON.stringify({ 
                    emails: contactsWithAccess.map(c => c.email),
                    accountId: customerDataInput.accountId,
                    excludeCustomerId: customerId // Escludi il cliente corrente in modifica
                })
            });
            
            if (!response.ok) return { hasConflict: false };
            
            const result = await response.json();
            return result;
        } catch (error) {
            console.error('[checkReservedAreaEmails] Errore:', error);
            return { hasConflict: false };
        }
    };
    
    // Stato per errori email duplicata
    const [emailConflictError, setEmailConflictError] = useState(null);
    
    // Funzione wrapper che controlla duplicati prima di salvare (sia creazione che modifica)
    const sendCustomerData = async () => {
        console.log('[sendCustomerData] Chiamata funzione, customerId:', customerId);
        setEmailConflictError(null);
        
        // Verifica unicità email area riservata (sia in creazione che modifica)
        const emailCheck = await checkReservedAreaEmails();
        if (emailCheck.hasConflict) {
            setEmailConflictError(emailCheck.conflictingEmails);
            setSendFetchStatus('f');
            setSendError(`Email già utilizzata per accesso area riservata: ${emailCheck.conflictingEmails.join(', ')}`);
            return;
        }
        
        // Controlla duplicati cliente (sia in creazione che modifica)
        console.log('[sendCustomerData] Controllo duplicati...');
        const hasDuplicates = await checkForDuplicates();
        console.log('[sendCustomerData] hasDuplicates:', hasDuplicates);
        if (!hasDuplicates) {
            // Nessun duplicato, procedi con il salvataggio
            console.log('[sendCustomerData] Nessun duplicato, procedo con performSave');
            await performSave();
        }
        // Se ci sono duplicati, il warning verrà mostrato e l'utente deciderà
    };
    
    // Gestisco il tasto Torna indietro in caso di provenienza da un'altra modale
    const rightModalOldState = useSelector(selectRightModalOldState);
    return (
        <div className="w-full p-4">
            {customerDetailsIsLoading && <LoadingSection isFixed={false} />}
            {customerDetailsError && <Error message={customerDetailsError} />}
            {(customerDetailsData || !customerId) && (
                <div className="card shadow-sm">
                    {/* Header della card con il titolo e tipo cliente */}
                    <div className="card-header d-flex justify-content-between align-items-center bg-light py-3">
                        <div>
                            <h4 className="mb-0 fw-bold">{customerId ? (isProspect ? 'Modifica Prospect' : 'Modifica Cliente') : (isProspect ? 'Nuovo Prospect' : 'Nuovo Cliente')}</h4>
                            {customerId && <small className="text-muted">ID: {customerId}</small>}
                        </div>
                        <span className={`badge ${customerDataInput?.business ? 'bg-success' : 'bg-primary'} p-2`}>
                            {customerDataInput?.business ? 'Business' : 'Privato'}
                        </span>
                    </div>
                    
                    <div className="card-body p-4">
                        {/* INFORMAZIONI */}
                        <div className="mb-4">
                            <h5 className="border-bottom pb-2 mb-3">
                                <i className="fa-solid fa-info-circle me-2 text-primary"></i>
                                Informazioni generali
                            </h5>
                            <div className="flex flex-col mb-4">
                                <div className="flex items-center gap-2 mb-3">
                                    <CustomToggleSwitch
                                        onChange={(boo) => setCustomerDataInput((prevState) => ({ ...prevState, business: boo }))}
                                        value={customerDataInput?.business ? true : false}
                                    />
                                    <label className="text-sm font-medium ml-2 lh-1">Cliente Business</label>
                                </div>
                                
                                {/* Lo switch per l'accesso all'area riservata è stato rimosso */}
                            </div>
                        </div>

                        {/* BUSINESS o PRIVATO */}
                        {customerDataInput?.business ? (
                            <div className="card mb-4 border-light">
                                <div className="card-body">
                                    {/* Ragione sociale e Tipo di Business */}
                                    <div className="row g-3 mb-3">
                                        <div className="col-md-6">
                                            <label htmlFor="company" className="form-label">Ragione Sociale</label>
                                            <input
                                                type="text"
                                                className="form-control"
                                                id="company"
                                                value={customerDataInput?.company || ""}
                                                onChange={handleCustomerDataInput}
                                                placeholder="Inserisci la ragione sociale"
                                            />
                                        </div>
                                        <div className="col-md-6">
                                            <label htmlFor="businessType" className="form-label">Tipo di Business</label>
                                            <CustomSelectElement
                                                id="businessType"
                                                name="businessType"
                                                list={accountData?.settings.customerIndustryTypes}
                                                setter={handleBusinessType}
                                                defaultValue={customerDataInput?.businessType}
                                                valueKey="_id"
                                                labelKey="label"
                                            />
                                        </div>
                                    </div>

                                    {/* Partita iva e codice fiscale */}
                                    <div className="row g-3 mb-3">
                                        <div className="col-md-6">
                                            <label htmlFor="vatNumber" className="form-label">Partita IVA</label>
                                            <div className="input-group">
                                                <span className="input-group-text"><i className="fa-solid fa-building"></i></span>
                                                <input
                                                    type="text"
                                                    className="form-control"
                                                    id="vatNumber"
                                                    value={customerDataInput?.vatNumber || ""}
                                                    onChange={handleCustomerDataInput}
                                                    placeholder="Inserisci la P.IVA"
                                                />
                                            </div>
                                        </div>
                                        <div className="col-md-6">
                                            <label htmlFor="fiscalcode" className="form-label">Codice Fiscale</label>
                                            <div className="input-group">
                                                <span className="input-group-text"><i className="fa-solid fa-id-card"></i></span>
                                                <input
                                                    type="text"
                                                    className="form-control"
                                                    id="fiscalcode"
                                                    value={customerDataInput?.fiscalcode || ""}
                                                    onChange={handleCustomerDataInput}
                                                    placeholder="Inserisci il codice fiscale"
                                                />
                                            </div>
                                        </div>
                                    </div>


                                </div>
                            </div>
                        ) : (
                            <div className="card mb-4 border-light">
                                <div className="card-body">
                                    {/* Blocco Privato */}
                                    {/* Nome e Cognome */}
                                    <div className="row g-3 mb-3">
                                        <div className="col-md-6">
                                            <label htmlFor="name" className="form-label">Nome</label>
                                            <div className="input-group">
                                                <span className="input-group-text"><i className="fa-solid fa-user"></i></span>
                                                <input
                                                    type="text"
                                                    className="form-control"
                                                    id="name"
                                                    value={customerDataInput?.name || ""}
                                                    onChange={handleCustomerDataInput}
                                                    placeholder="Inserisci il nome"
                                                />
                                            </div>
                                        </div>
                                        <div className="col-md-6">
                                            <label htmlFor="surname" className="form-label">Cognome</label>
                                            <div className="input-group">
                                                <span className="input-group-text"><i className="fa-solid fa-user"></i></span>
                                                <input
                                                    type="text"
                                                    className="form-control"
                                                    id="surname"
                                                    value={customerDataInput?.surname || ""}
                                                    onChange={handleCustomerDataInput}
                                                    placeholder="Inserisci il cognome"
                                                />
                                            </div>
                                        </div>
                                    </div>
                                    <div className="mb-3">
                                        <label htmlFor="fiscalcode" className="form-label">Codice Fiscale</label>
                                        <div className="input-group">
                                            <span className="input-group-text"><i className="fa-solid fa-id-card"></i></span>
                                            <input
                                                type="text"
                                                className="form-control"
                                                id="fiscalcode"
                                                value={customerDataInput?.fiscalcode || ""}
                                                onChange={handleCustomerDataInput}
                                                placeholder="Inserisci il codice fiscale"
                                            />
                                        </div>
                                    </div>
                                </div>
                            </div>
                        )}

                        {/* Sezione indirizzi */}
                        <div className="card mb-4 border-0 shadow-sm" data-section="addresses">
                            <div className="card-header bg-light">
                                <h5 className="mb-0"><i className="fa-solid fa-map-marker-alt me-2 text-primary"></i>Indirizzi {!customerId && <span className="text-danger">*</span>}</h5>
                            </div>
                            <div className="card-body p-0">
                                <CustomerAddressesPool arrayData={customerDataInput?.addresses} addEditAddress={addEditItem} deleteAddress={deleteItem} />
                            </div>
                        </div>
                        
                        {/* Sezione contatti */}
                        <div className="card mb-4 border-0 shadow-sm">
                            <div className="card-header bg-light">
                                <h5 className="mb-0"><i className="fa-solid fa-address-book me-2 text-primary"></i>Contatti</h5>
                            </div>
                            <div className="card-body p-0">
                                <CustomerContactsPool 
                                    arrayData={customerDataInput?.contactsList} 
                                    addEditContact={addEditItem} 
                                    deleteContact={deleteItem}
                                    accountId={customerDataInput?.accountId}
                                    customerId={customerId}
                                />
                            </div>
                        </div>

                        {/* Sezione pagamenti - mostrata solo per clienti business */}
                        {customerDataInput?.business && (
                            <div className="card mb-4 border-0 shadow-sm">
                                <div className="card-header bg-light">
                                    <h5 className="mb-0"><i className="fa-solid fa-credit-card me-2 text-primary"></i>Pagamenti</h5>
                                </div>
                                <div className="card-body p-0">
                                    <CustomerBanksPool arrayData={customerDataInput?.banksList} addEditBank={addEditItem} deleteBank={deleteItem} />
                                </div>
                            </div>
                        )}

                        {/* Dati fiscali elettronici (PEC per tutti, SDI solo per business) */}
                        <div className="card mb-4 border-light">
                            <div className="card-header bg-light">
                                <h5 className="mb-0"><i className="fa-solid fa-envelope me-2 text-primary"></i>Dati fiscali elettronici</h5>
                            </div>
                            <div className="card-body">
                                <div className="row g-3">
                                    {customerDataInput?.business && (
                                        <div className="col-md-6">
                                            <label htmlFor="sdiCode" className="form-label">Codice SDI</label>
                                            <div className="input-group">
                                                <span className="input-group-text"><i className="fa-solid fa-hashtag"></i></span>
                                                <input
                                                    type="text"
                                                    className="form-control"
                                                    id="sdiCode"
                                                    value={customerDataInput?.sdiCode || ""}
                                                    onChange={handleCustomerDataInput}
                                                    placeholder="Inserisci il codice SDI"
                                                />
                                            </div>
                                        </div>
                                    )}
                                    <div className={customerDataInput?.business ? "col-md-6" : "col-12"}>
                                        <label htmlFor="pec" className="form-label">PEC</label>
                                        <div className="input-group">
                                            <span className="input-group-text"><i className="fa-solid fa-at"></i></span>
                                            <input
                                                type="text"
                                                className="form-control"
                                                id="pec"
                                                value={customerDataInput?.pec || ""}
                                                onChange={handleCustomerDataInput}
                                                placeholder="Inserisci l'indirizzo PEC"
                                            />
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>

                        {/* Warning duplicati - mostrato sopra il footer */}
                        {showDuplicateModal && duplicates.length > 0 && (
                            <div className="alert alert-warning mx-3 mb-0" role="alert">
                                <div className="d-flex align-items-start">
                                    <i className="fa-solid fa-exclamation-triangle me-3 mt-1 fs-4"></i>
                                    <div className="flex-grow-1">
                                        <h6 className="alert-heading mb-2">
                                            <strong>Attenzione: Trovati {duplicates.length} possibili clienti duplicati</strong>
                                        </h6>
                                        <div style={{ maxHeight: '200px', overflowY: 'auto' }}>
                                            {duplicates.slice(0, 5).map((dup, index) => (
                                                <div key={index} className="mb-2 p-2 bg-white rounded border">
                                                    <div className="d-flex justify-content-between align-items-center">
                                                        <span>
                                                            <strong>
                                                                {dup.existingCustomer.business 
                                                                    ? dup.existingCustomer.company 
                                                                    : `${dup.existingCustomer.name || ''} ${dup.existingCustomer.surname || ''}`}
                                                            </strong>
                                                            {dup.existingCustomer.code && (
                                                                <span className="text-muted ms-2">(Cod: {dup.existingCustomer.code})</span>
                                                            )}
                                                        </span>
                                                        <span className={`badge ${dup.maxSimilarity === 1 ? 'bg-danger' : 'bg-warning text-dark'}`}>
                                                            {Math.round(dup.maxSimilarity * 100)}%
                                                        </span>
                                                    </div>
                                                    <small className="text-muted">
                                                        {dup.matchReasons.map(r => r.message).join(' | ')}
                                                    </small>
                                                </div>
                                            ))}
                                            {duplicates.length > 5 && (
                                                <div className="text-muted text-center">
                                                    <small>...e altri {duplicates.length - 5} risultati</small>
                                                </div>
                                            )}
                                        </div>
                                        <hr className="my-2" />
                                        <div className="d-flex gap-2">
                                            <button 
                                                className="btn btn-sm btn-outline-secondary"
                                                onClick={() => setShowDuplicateModal(false)}
                                            >
                                                <i className="fa-solid fa-times me-1"></i>
                                                Annulla e verifica
                                            </button>
                                            <button 
                                                className="btn btn-sm btn-warning"
                                                onClick={handleProceedWithSave}
                                            >
                                                <i className="fa-solid fa-check me-1"></i>
                                                {customerId ? 'Procedi comunque con la modifica' : 'Procedi comunque con la creazione'}
                                            </button>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        )}

                        {/* Conferma propagazione correzione - mostrato sopra il footer */}
                        {showPropagationModal && propagationImpact && (
                            <div className="alert alert-info mx-3 mb-0" role="alert">
                                <div className="d-flex align-items-start">
                                    <i className="fa-solid fa-circle-info me-3 mt-1 fs-4"></i>
                                    <div className="flex-grow-1">
                                        <h6 className="alert-heading mb-2">
                                            <strong>Conferma correzione anagrafica</strong>
                                        </h6>
                                        <p className="mb-2">
                                            Questa correzione verrà propagata ai seguenti elementi collegati:
                                        </p>
                                        <ul className="mb-2">
                                            {propagationImpact.impianti > 0 && (
                                                <li><strong>{propagationImpact.impianti}</strong> {propagationImpact.impianti === 1 ? 'impianto' : 'impianti'}</li>
                                            )}
                                            {propagationImpact.commesse > 0 && (
                                                <li><strong>{propagationImpact.commesse}</strong> {propagationImpact.commesse === 1 ? 'commessa' : 'commesse'}</li>
                                            )}
                                            {propagationImpact.assistenze > 0 && (
                                                <li><strong>{propagationImpact.assistenze}</strong> {propagationImpact.assistenze === 1 ? 'assistenza' : 'assistenze'}</li>
                                            )}
                                            {propagationImpact.manutenzioni > 0 && (
                                                <li><strong>{propagationImpact.manutenzioni}</strong> {propagationImpact.manutenzioni === 1 ? 'manutenzione' : 'manutenzioni'}</li>
                                            )}
                                            {propagationImpact.appuntamenti > 0 && (
                                                <li><strong>{propagationImpact.appuntamenti}</strong> {propagationImpact.appuntamenti === 1 ? 'appuntamento' : 'appuntamenti'}</li>
                                            )}
                                        </ul>
                                        <p className="mb-2 text-muted">
                                            <small>
                                                La modifica è trattata come una <strong>correzione</strong> del dato: se hai cambiato l'indirizzo verranno
                                                aggiornate anche le coordinate e la distanza dalla sede (km/minuti) sugli elementi collegati.
                                                Se invece si tratta di un cambiamento reale (nuova ragione sociale, sede trasferita),
                                                annulla e crea una nuova anagrafica o un nuovo indirizzo.
                                            </small>
                                        </p>
                                        <hr className="my-2" />
                                        <div className="d-flex gap-2">
                                            <button
                                                className="btn btn-sm btn-outline-secondary"
                                                onClick={() => setShowPropagationModal(false)}
                                            >
                                                <i className="fa-solid fa-times me-1"></i>
                                                Annulla
                                            </button>
                                            <button
                                                className="btn btn-sm btn-primary"
                                                onClick={handleConfirmPropagation}
                                            >
                                                <i className="fa-solid fa-check me-1"></i>
                                                Procedi con la correzione
                                            </button>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        )}

                        {/* Footer con pulsanti e stato */}
                        <div className="card-footer bg-light d-flex justify-content-between align-items-center p-3 mt-3">
                            <div className="d-flex align-items-center">
                                {/* Status messages */}
                                {sendFetchStatus === 'l' && (
                                    <div className="d-flex align-items-center text-info">
                                        <div className="spinner-border spinner-border-sm me-2" role="status">
                                            <span className="visually-hidden">Caricamento...</span>
                                        </div>
                                        <span>Salvataggio in corso...</span>
                                    </div>
                                )}
                                {sendFetchStatus === 'f' && (
                                    <div className="text-danger d-flex align-items-center">
                                        <i className="fa-solid fa-exclamation-circle me-2"></i>
                                        {sendError}
                                    </div>
                                )}
                                {sendFetchStatus === 's' && (
                                    <div className="text-success d-flex align-items-center">
                                        <i className="fa-solid fa-check-circle me-2"></i>
                                        {isProspect ? 'Prospect' : 'Cliente'} {customerId ? 'aggiornato' : 'creato'} con successo
                                    </div>
                                )}
                                {duplicateCheckLoading && (
                                    <div className="d-flex align-items-center text-warning">
                                        <div className="spinner-border spinner-border-sm me-2" role="status">
                                            <span className="visually-hidden">Verifica...</span>
                                        </div>
                                        <span>Verifica duplicati in corso...</span>
                                    </div>
                                )}
                                {propagationCheckLoading && (
                                    <div className="d-flex align-items-center text-info">
                                        <div className="spinner-border spinner-border-sm me-2" role="status">
                                            <span className="visually-hidden">Verifica...</span>
                                        </div>
                                        <span>Verifica elementi collegati in corso...</span>
                                    </div>
                                )}
                            </div>
                            <div>
                                <button
                                    className="btn btn-outline-secondary me-2"
                                    onClick={() => rightModalOldState?.oldMode ? dispatch(swapModal()) : dispatch(setGoingToClose())}
                                >
                                    <i className="fa-solid fa-times me-1"></i>
                                    Annulla
                                </button>
                                <button
                                    className="btn btn-primary"
                                    disabled={sendFetchStatus === 'l' || duplicateCheckLoading || showDuplicateModal || propagationCheckLoading || showPropagationModal}
                                    onClick={() => {
                                        console.log('[Button Click] Pulsante Salva cliccato');
                                        sendCustomerData();
                                    }}
                                >
                                    <i className="fa-solid fa-save me-1"></i>
                                    {customerId ? (isProspect ? 'Modifica prospect' : 'Modifica cliente') : 'Salva'}
                                </button>
                                {rightModalOldState.oldMode && rightModalOldState.oldId && rightModalOldState.oldPercentuage && (
                                    <button
                                        onClick={() => {
                                            dispatch(setSwap({
                                                id: rightModalOldState.oldId,
                                                mode: rightModalOldState.oldMode,
                                                percentuageWidth: rightModalOldState.oldPercentuage,
                                                isOld: false
                                            }))
                                        }}
                                        className="btn btn-secondary ms-2"
                                        disabled={sendFetchStatus === 'l'}
                                    >
                                        <i className="fa-solid fa-arrow-left me-1"></i>
                                        Torna indietro
                                    </button>
                                )}
                            </div>
                        </div>
                    </div>
                </div>
            )}
            
        </div>
    );
}
